//initial redux
import { put, call, takeEvery, fork, take, all, takeLatest, select } from "redux-saga/effects";
import { delay } from "redux-saga";
import { types as sitetreetypes } from "../../../services/SiteTree/redux";
import { types as tarifftypes } from "../../Tariffs/redux";
import { types as livetypes } from "../../../ui/Live/redux";
import moment from "moment";
import { cloneAsync, number } from ".././../../utils";
import pointsservice from "../../../services/points";
import takeLeading from "../../../utils/takeLeading";

const LIVE_DATA_INTERVAL = 5000;
const FAKE_VALUE_INTERVAL = 1000;

const initialState = {
	meterTree: null,
	onPage: false,
	isBusy: false,
	errors: [],
	hasError: false,
	forceBusy: true,
};

/////////
//types
export const types = {
	LOAD_TREE: "components/metertree/LOAD_TREE",
	UPDATE_TREE: "components/metertree/UPDATE_TREE",
	START_LIVE_DATA: "screens/metertree/START_LIVE_DATA",
	STOP_LIVE_DATA: "screens/metertree/STOP_LIVE_DATA",
    UPDATE_LIVE_DATA: "screens/metertree/UPDATE_LIVE_DATA",
    START_FAKER: "screens/metertree/START_FAKER",
    UPDATE_FAKE_DATA: "screens/metertree/UPDATE_FAKE_DATA"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
			case types.LOAD_TREE: {
				return state;
			}
			case types.UPDATE_TREE: {
				return { ...state, meterTree: action.payload.meterTree, forceBusy: false };
			}
			case types.START_LIVE_DATA: {
				return { ...state, onPage: true };
			}
			case types.STOP_LIVE_DATA: {
				return { ...state, onPage: false };
			}
			case types.UPDATE_LIVE_DATA: {
				return state;
            }
            case types.UPDATE_FAKE_DATA: {
                return state;
            }
			case tarifftypes.SET_TARIFF:
			case livetypes.SELECT_TYPE:
				return { ...state, forceBusy: true }
			default:
				return state;
		}
	}
};

function loadChildren(currentView, sitesDataMap, pointType) {
	let children = [];

	if (currentView._tree.children) {
		for (var ii = 0; ii < currentView._tree.children.length; ii++) {
			let childView = sitesDataMap[currentView._tree.children[ii]];
			if (childView && childView.type === "View") {
				children.push({
					name: childView.name,
					virtual: childView.name.toLowerCase().indexOf("virtual") !== -1,
					value: 0,
					viewId: childView.objectID,
					children: loadChildren(childView, sitesDataMap, pointType),
					pointType: pointType
				});
			}
		}
	}

	return children;
}

function findHeights(currentView) {
	let wkHeight = 0;

	if (!currentView.children || !currentView.children.length) {
		wkHeight = 1;
	} else {
		for (var ii = 0; ii < currentView.children.length; ii++) {
			wkHeight += findHeights(currentView.children[ii]);
		}
	}

	currentView.height = wkHeight;

	return wkHeight;
}

function getMeterTree(sitesDataMap, rootSite, meterGroupName, startPointName, pointType) {
	let meterGroup = null;
	for (var ii = 0; ii < rootSite._tree.children.length; ii++) {
		if (sitesDataMap[rootSite._tree.children[ii]].name === meterGroupName) {
			meterGroup = sitesDataMap[rootSite._tree.children[ii]];
			break;
		}
	}

	if (!meterGroup || !meterGroup._tree.children) {
		return null;
	}

	let startView = null;
	for (var ii = 0; ii < meterGroup._tree.children.length; ii++) {
		if (sitesDataMap[meterGroup._tree.children[ii]].name === startPointName) {
			startView = sitesDataMap[meterGroup._tree.children[ii]];
			break;
		}
	}

	if (!startView) {
		return null;
	}

	let meterTree = {
		name: startView.name,
		virtual: false,
		viewId: startView.objectID,
		value: 0,
		children: loadChildren(startView, sitesDataMap, pointType),
		root: true,
		pointType: pointType
	};

	return meterTree;
}

function combineTrees(baseTree, otherTrees) {
	if (!baseTree) {
		return;
	}
	if (!otherTrees) {
		otherTrees = [];
	}

	baseTree[baseTree.pointType + "PointId"] = baseTree.viewId;
	for (var ii = 0; ii < otherTrees.length; ii++) {
		let wktree = otherTrees[ii];
		baseTree[wktree.pointType + "PointId"] = wktree.viewId;
	}

	if (!baseTree.children || !baseTree.children.length) {
		return;
	}

	for (var ii = 0; ii < baseTree.children.length; ii++) {
		var wkBaseChild = baseTree.children[ii];
		var otherTreeChildren = [];
		otherTrees.forEach(otherTree => {
			if (otherTree.children && otherTree.children.length) {
				otherTree.children.forEach(otherTreeChild => {
					if (otherTreeChild.name === wkBaseChild.name) {
						otherTreeChildren.push(otherTreeChild);
					}
				});
			}
		});
		combineTrees(wkBaseChild, otherTreeChildren);
	}
}

function* loadCost(meterTree) {
	const currentTariff = yield select(x => x.tariffs.currentTariff);
	const currentTariffVersion = yield select(x => x.tariffs.currentTariffVersion);
	if (!currentTariff) {
		meterTree.value = 0;
		return;
	}
	const rtns = yield call(
		pointsservice.LiveDataGetRollingHour,
		meterTree.powerUsagePointId,
		currentTariff.tariffPK,
		currentTariffVersion,
		true,
		false,
		null
	);
	const costPerTariffPeriod = rtns[meterTree.powerUsagePointId];
	if (costPerTariffPeriod) { 
		meterTree.value = costPerTariffPeriod.value;
		meterTree.secondIncrement = costPerTariffPeriod.deltaPerSecondForecast;
	}
}

function* loadPowerUsage(meterTree) {
	const rtns = yield call(
		pointsservice.LiveDataGetRollingHour,
		meterTree.powerUsagePointId,
		null,
		null,
		true,
		false,
		null
	);
	const powerUsage = rtns[meterTree.powerUsagePointId];
	if(powerUsage) {
		meterTree.value = powerUsage.value;
		meterTree.secondIncrement = powerUsage.deltaPerSecondForecast;
	}
}

function* loadPowerRate(meterTree) {
	let fromDate = moment().subtract(1, "hour");
	let toDate = moment();
	const currentUsage = yield call(
		pointsservice.LiveDataGet,
		meterTree.powerRatePointId,
	);
    let usage = 0;
    let secondIncrement = 0;
	if (currentUsage && currentUsage[meterTree.powerRatePointId]) {
		usage = currentUsage[meterTree.powerRatePointId].value;
        usage = usage ? usage : 0;
        secondIncrement = currentUsage[meterTree.powerRatePointId].deltaPerSecondForecast;
        secondIncrement = secondIncrement ? secondIncrement : 0;
  }
    meterTree.value = usage;
    meterTree.secondIncrement = secondIncrement;
}

function* loadCarbonImpact(meterTree) {
	let currentSite = yield select(x => x.sitetree.currentSite);
	const carbonImpact = yield call(
		pointsservice.LiveDataGetRollingHour,
		meterTree.powerUsagePointId,
		null,
		null,
		true,
		true,
		currentSite.state ? currentSite.state : null
	);
	const carbon = carbonImpact[meterTree.powerUsagePointId];
	if(carbon) {
		meterTree.value = carbon.value;
		meterTree.secondIncrement = carbon.deltaPerSecondForecast;
	}
}

function* updateMeterData(meterTree, units) {
	switch (units) {
		case "$": {
			yield call(loadCost, meterTree);
			break;
		}
		case "kWh": {
			yield call(loadPowerUsage, meterTree);
			break;
		}
		case "kW": {
			yield call(loadPowerRate, meterTree);
			break;
		}
		case "kgCO₂": {
			yield call(loadCarbonImpact, meterTree);
			break;
		}
		default: {
			break;
		}
	}
}

function* updateData(meterTree, units) {
	let requests = [];
	meterTree.forEach(tree => {
		tree.blocks.forEach(block => {
			if (block.type !== "blank") {
				requests.push(call(updateMeterData, block, units));
			}
		});
	});
	yield all(requests);
}

function figureDepth(wkNode) {
	let depth = [];
	let childDepth = wkNode.children.length;
	depth.push(childDepth);
	let grandChildDepths = [];
	for (var ii = 0; ii < wkNode.children.length; ii++) {
		grandChildDepths.push(figureDepth(wkNode.children[ii]));
	}
	let maxDepth = 0;
	for (var ii = 0; ii < grandChildDepths.length; ii++) {
		if (grandChildDepths[ii].length > maxDepth) {
			maxDepth = grandChildDepths[ii].length;
		}
	}
	for (var ii = 0; ii < maxDepth; ii++) {
		let wkHeightDepth = 0;
		for (var jj = 0; jj < grandChildDepths.length; jj++) {
			let wkDepth = grandChildDepths[jj][ii];
			wkHeightDepth += wkDepth ? wkDepth : 0;
		}
		depth.push(wkHeightDepth);
	}
	wkNode.depth = depth;
	return depth;
}

function getMaxDepth(powerUsageTree) {
	let depth = 1;
	let maxChildDepth = 0;
	for (var ii = 0; ii < powerUsageTree.children.length; ii++) {
		let childDepth = getMaxDepth(powerUsageTree.children[ii]);
		if (childDepth > maxChildDepth) {
			maxChildDepth = childDepth;
		}
	}
	return depth + maxChildDepth;
}

function addPipes(wkColumn, index) {
	let firstPipe = index === 1 && wkColumn.pipes.length === 0;
	let newPipe = null;
	for (var ii = 0; ii < wkColumn.blocks.length; ii++) {
		let wkBlock = wkColumn.blocks[ii];
		//if block is blank do nothing
		if (wkBlock.type === "blank") {
			continue;
		}
		//if newpipe is null then create it with the current inderx and block type
		if (!newPipe) {
			newPipe = {
				startIndex: ii,
				types: []
			};
			if (firstPipe && wkBlock.type !== "virtual") {
				newPipe.types.push("main");
			} else {
				newPipe.types.push(wkBlock.type);
			}
		} else {
			//if the current block isn't new then find the first block beofre the current one that is not a blank block
			let previousNonBlank = null;
			let blankCount = 0;
			for (var jj = ii - 1; jj >= 0; jj--) {
				let wkPrevBlock = wkColumn.blocks[jj];
				if (wkPrevBlock.type !== "blank") {
					previousNonBlank = wkPrevBlock;
					break;
				} else {
					blankCount++;
				}
			}
			//ifthe last non blank blocks parent matches the current blocks parent then it's part of the same pipe
			if (previousNonBlank && wkBlock.parentViewId && previousNonBlank.parentViewId === wkBlock.parentViewId) {
				//make sure to add in the blank types to that the pipe is the right height.
				//if there are blanks to add use teh types of teh previous and current to fill out the array (these determine color later on)
				if (blankCount) {
					let previousTypeCount = Math.ceil(blankCount / 2);
					let wkTypeCount = Math.floor(blankCount / 2);
					for (var jj = 0; jj < previousTypeCount; jj++) {
						if (firstPipe && previousNonBlank !== "virtual") {
							newPipe.types.push("main");
						} else {
							newPipe.types.push(previousNonBlank.type);
						}
					}
					for (var jj = 0; jj < wkTypeCount; jj++) {
						if (newPipe && wkBlock.type !== "virtual") {
							newPipe.types.push("main");
						} else {
							newPipe.types.push(wkBlock.type);
						}
					}
				}
				//finally add teh working blocks type
				if (firstPipe && wkBlock.type !== "virtual") {
					newPipe.types.push("main");
				} else {
					newPipe.types.push(wkBlock.type);
				}
			} else {
				//different parents. Add the current pipe in if suitable and start a new one.
				if (newPipe && newPipe.types.length > 1) {
					wkColumn.pipes.push(newPipe);
				}
				newPipe = {
					startIndex: ii,
					types: [wkBlock.type]
				};
			}
		}
	}
	if (newPipe && newPipe.types.length > 1) {
		wkColumn.pipes.push(newPipe);
	}
}

function getColumns(powerUsageTree) {
	figureDepth(powerUsageTree);
	let maxDepth = getMaxDepth(powerUsageTree);
	let columns = [];
	for (var ii = 0; ii < maxDepth; ii++) {
		columns.push({
			pipes: [],
			blocks: []
		});
	}
	addChildColumn(powerUsageTree, null, columns, 0, null);
	for (var ii = 0; ii < columns.length; ii++) {
		addPipes(columns[ii], ii);
	}
	return columns;
}

function addChildColumn(wkTree, previousTree, columns, height, parent) {
	if (!previousTree && parent) {
		let currentDepthAtHeight = columns[height].blocks.length;
		let parentArray = columns[height - 1].blocks;
		let parentDepth = parentArray.indexOf(parent);
		let blankCount = parentDepth - currentDepthAtHeight;
		for (var ii = 0; ii < blankCount; ii++) {
			columns[height].blocks.push({
				type: "blank"
			});
		}
	}
	if (previousTree) {
		let prevMaxDepth = 1;
		for (var ii = 0; ii < wkTree.depth.length; ii++) {
			if (wkTree.depth[ii] === 0) {
				break;
			}
			let wkTreeIndexInParent = parent.children.indexOf(wkTree);
			for(var jj = 0; jj < wkTreeIndexInParent; jj++){
				let previousTreeDepth = parent.children[jj].depth[ii];
				let offset = wkTreeIndexInParent - jj - 1;
				previousTreeDepth = previousTreeDepth ? previousTreeDepth : 0;
				if ((previousTreeDepth - offset) > prevMaxDepth) {
					prevMaxDepth = previousTreeDepth - offset;
				}
			}
		}
		let blankCount = prevMaxDepth - 1;
		for (var ii = 0; ii < blankCount; ii++) {
			columns[height].blocks.push({
				type: "blank",
				firstChildType: "none",
				parentType: "none"
			});
		}
	}
	wkTree.type = wkTree.root ? "main" : wkTree.virtual ? "virtual" : "standard";
	wkTree.firstChildType = "none";
	if (wkTree.children && wkTree.children.length) {
		let child = wkTree.children[0];
		let childType = child.root ? "main" : child.virtual ? "virtual" : "standard";
		wkTree.firstChildType = childType;
	}
	wkTree.parentType = parent ? parent.type : "none";
	wkTree.parentViewId = parent ? parent.viewId : null;
	wkTree.firstChild = parent && parent.children.indexOf(wkTree) === 0;
	wkTree.lastChild = parent && parent.children.indexOf(wkTree) === parent.children.length - 1;
	wkTree.onlyChild = parent && parent.children.length === 1;

	columns[height].blocks.push(wkTree);
	if (wkTree.children) {
		for (var ii = 0; ii < wkTree.children.length; ii++) {
			addChildColumn(wkTree.children[ii], wkTree.children[ii - 1], columns, height + 1, wkTree);
		}
	}
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
        yield all([this.loadTree(),
            this.startLiveDataInterval(),
            this.updateLiveData(),
            this.startFaker(),
            this.updateFakeData()]);
	},
    *startLiveDataInterval() {
		for (;;) {
			yield delay(LIVE_DATA_INTERVAL);
			const onPage = yield select(x => x.live.onPage);
			if (onPage) {
				yield put(actions.updateLiveData());
			} else {
				return;
			}
		}
	},
	*updateLiveData() {
		yield takeLeading([types.UPDATE_LIVE_DATA, tarifftypes.SET_TARIFF, livetypes.SELECT_TYPE], function*() {
            let meterTree = yield select(x => x.metertree.meterTree);
			let units = yield select(x => x.live.selectedType);
			if (meterTree && units) {
				let clonedTree = yield call(cloneAsync, meterTree);
				yield call(updateData, clonedTree, units);
				yield put(actions.updateTree(clonedTree));
            }
		});
    },
    *startFaker() {
        yield takeLatest(types.START_FAKER, function* () {
            for (; ;) {
                const onPage = yield select(x => x.live.onPage);
                if (onPage) {
                    yield put(actions.updateFakeData());
                } else {
                    break;
                }
                yield delay(FAKE_VALUE_INTERVAL);
            }
        });
    },
    *updateFakeData() {
        yield takeLatest(types.UPDATE_FAKE_DATA, function* () {
            let meterTree = yield select(x => x.metertree.meterTree);
            let units = yield select(x => x.live.selectedType);
            if (meterTree && units) {
                let clonedTree = yield call(cloneAsync, meterTree);
                
                clonedTree.forEach(tree => {
                    tree.blocks.forEach(block => {
                        if (block.type !== "blank" && block.value !== null && block.value !== 0 && block.secondIncrement !== null) {
                            block.value = Number(block.value) + Number(block.secondIncrement || 0);
                        }
                    });
                });

                yield put(actions.updateTree(clonedTree));
            }
        });
    },
	*loadTree() {
		yield takeLatest([sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD], function*() {
			let sitesDataMap = yield select(x => x.sitetree.flattenedSiteTree);
			let currentSite = yield select(x => x.sitetree.currentSite);
			if (
				!sitesDataMap ||
				!currentSite ||
				!currentSite._tree ||
				!currentSite._tree.children ||
				!currentSite._tree.children.length
			) {
				yield put(actions.updateTree(null));
				return;
			}

			//root starting view for all trees
			let startPointName = "Main";
			//starting point for each tree type. kwh, kw, kgco2 etc
			let powerUsageGroupName = "Meters";
			let powerRateGroupName = "Meters.kW";
            let carbonImpactGroupName = "Meters";

			//now load all the trees
			let powerUsageTree = getMeterTree(
				sitesDataMap,
				currentSite,
				powerUsageGroupName,
				startPointName,
				"powerUsage"
			);
			let powerRateTree = getMeterTree(
				sitesDataMap,
				currentSite,
				powerRateGroupName,
				startPointName,
				"powerRate"
			);
			let carbonImpactTree = getMeterTree(
				sitesDataMap,
				currentSite,
				carbonImpactGroupName,
				startPointName,
				"carbonImpact"
			);

			if (!powerUsageTree) {
				yield put(actions.updateTree(null));
				return;
			}

			let otherTrees = [];
			if (powerRateTree) {
				otherTrees.push(powerRateTree);
			}

			if (carbonImpactTree) {
				otherTrees.push(carbonImpactTree);
			}

			combineTrees(powerUsageTree, otherTrees);

			findHeights(powerUsageTree);

			let columns = getColumns(powerUsageTree);

			yield put(actions.updateTree(columns));
			
			const onPage = yield select(x => x.live.onPage);
			if(onPage){
				yield put(actions.updateLiveData());
			}
		});
	}
};

////////
//actions
export const actions = {
	loadTree() {
		return {
			type: types.LOAD_TREE,
			payload: {}
		};
	},
	updateTree(meterTree) {
		return {
			type: types.UPDATE_TREE,
			payload: { meterTree }
		};
	},
	startLiveData() {
		return {
			type: types.START_LIVE_DATA,
			payload: {}
		};
	},
	stopLiveData() {
		return {
			type: types.STOP_LIVE_DATA,
			payload: {}
		};
	},
	updateLiveData() {
		return {
			type: types.UPDATE_LIVE_DATA,
			payload: {}
		};
    },
    startFaker() {
        return {
            type: types.START_FAKER,
        }
    },
    updateFakeData() {
        return {
            type: types.UPDATE_FAKE_DATA,
        }
    }
};



// WEBPACK FOOTER //
// ./src/components/LiveComponents/MeterTree/redux.js